import { useState } from 'react';
import type {
  DocumentTypeLibraryResponse,
  EmployeeDocumentDownloadResponse,
  EmployeeDocumentResponse,
  EmployeeDocumentsResponse,
  EmployeeProfileResponse,
} from '../../api/types';
import { apiGet, apiPost, apiUpload, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import { formatCalendarDate, formatTimestamp } from './attributeDisplay';
import listStyles from './FactRecords.module.css';
import factStyles from './CaptureSection.module.css';
import formStyles from './AssessmentForm.module.css';
import styles from './DocumentsSection.module.css';

const CATEGORY_LABEL: Record<string, string> = { identity: 'Identity', qualification: 'Qualification' };
const ACCEPTED_TYPES = 'application/pdf,image/jpeg,image/png';

/**
 * Employee Documents (Mission 168, "Visual Identity & Employee Media"
 * MVP, approved decision brief §3/§4/§5/§6/§7).
 *
 * ---------------------------------------------------------------------
 * A SEPARATE CONCEPT FROM EVIDENCE
 *
 * This is administrative/compliance material about the person (Identity,
 * Qualification documents in this MVP), never evidence supporting a
 * judgement about them — see `docs/ROADMAP.md`'s own written boundary.
 * This section shares nothing with the Evidence attachment flow
 * elsewhere in this profile.
 *
 * SELF, ADMIN, OR HR PARTNER — NEVER A MANAGER
 *
 * `canManage` (derived from `profile.viewerCanManageDocuments`, decided
 * server-side, never guessed at here) gates every write affordance; a
 * manager reading a report's profile sees this section rendered the
 * same as anyone else without visibility at all — omitted entirely by
 * the API, not merely hidden by this component (§5: managers have no
 * document visibility in this MVP, full stop).
 *
 * EXPIRY IS SHOWN, NEVER PUSHED
 *
 * `isExpired` is a plain, already-computed fact this component renders
 * with the new warning/danger tokens (§8) — no notification, no
 * scheduled job; see the field's own doc comment
 * (`domain/src/models/employeeDocument.ts`).
 */
export function DocumentsSection({ profile }: { profile: EmployeeProfileResponse }) {
  const canManage = profile.viewerCanManageDocuments;
  const documents = useApiResource<EmployeeDocumentsResponse>(`/employees/${profile.id}/documents`);
  const documentTypes = useApiResource<DocumentTypeLibraryResponse>('/document-types');
  const [uploading, setUploading] = useState(false);

  return (
    <>
      <FactSection
        id="documents"
        title="Documents"
        state={documents}
        loadingLabel="Loading documents…"
        errorTitle="Documents could not be loaded"
        isEmpty={(data) => data.documents.length === 0}
        empty={{
          title: 'No documents on file',
          detail: canManage
            ? 'Nothing has been uploaded yet. Use "Upload document" below to add one.'
            : 'Nothing has been uploaded here yet. That is an absence of a record, not a finding about this person.',
        }}
        note="Identity and qualification documents on file for this person — separate from Evidence, which supports a specific assessment, not a standalone document library."
      >
        {(data) => (
          <ul className={listStyles.list}>
            {data.documents.map((document) => (
              <DocumentCard
                key={document.id}
                employeeId={profile.id}
                document={document}
                canManage={canManage}
                onChanged={() => documents.reload()}
              />
            ))}
          </ul>
        )}
      </FactSection>

      {canManage && documents.status === 'loaded' ? (
        documentTypes.status === 'loaded' ? (
          uploading ? (
            <DocumentUploadForm
              employeeId={profile.id}
              documentTypes={documentTypes.data.documentTypes.filter((t) => t.status === 'active')}
              onDone={() => {
                setUploading(false);
                documents.reload();
              }}
              onClose={() => setUploading(false)}
            />
          ) : (
            <button type="button" className={factStyles.addToggle} onClick={() => setUploading(true)}>
              Upload document
            </button>
          )
        ) : (
          <p className={listStyles.notes}>Loading document types…</p>
        )
      ) : null}
    </>
  );
}

function DocumentCard({
  employeeId,
  document,
  canManage,
  onChanged,
}: {
  employeeId: string;
  document: EmployeeDocumentResponse;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [replacing, setReplacing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');
  const [state, setState] = useState<{ status: 'idle' } | { status: 'saving' } | { status: 'failed'; message: string }>({
    status: 'idle',
  });
  const [downloadState, setDownloadState] = useState<{ status: 'idle' } | { status: 'opening' } | { status: 'failed'; message: string }>({
    status: 'idle',
  });

  async function handleRemove() {
    if (reason.trim().length === 0) return;
    setState({ status: 'saving' });
    try {
      await apiPost(`/employees/${employeeId}/documents/${document.id}/removal`, { reason: reason.trim() });
      onChanged();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  /** Mints a fresh short-lived signed URL (never a public object-storage
   * URL — `api/src/routes/employees.ts`'s own download route, backed by
   * the same self/admin/hr_partner `authorizeEmployeeDocumentAccess` gate
   * as every other document route) and opens it directly — never cached,
   * never reused across clicks. */
  async function handleDownload() {
    setDownloadState({ status: 'opening' });
    try {
      const download = await apiGet<EmployeeDocumentDownloadResponse>(`/employees/${employeeId}/documents/${document.id}/download`);
      window.open(download.url, '_blank', 'noopener');
      setDownloadState({ status: 'idle' });
    } catch (error) {
      setDownloadState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <li className={listStyles.record}>
      <div className={listStyles.primary}>
        <span className={listStyles.name}>{document.documentTypeName}</span>
        <span className={listStyles.marker}>{document.status === 'active' ? 'Current' : document.status}</span>
        {document.isExpired ? <span className={styles.expiredBadge}>Expired</span> : null}
      </div>

      <dl className={listStyles.facts}>
        {document.issueDate ? (
          <div>
            <dt>Issued</dt>
            <dd>{formatCalendarDate(document.issueDate)}</dd>
          </div>
        ) : null}
        {document.expiryDate ? (
          <div>
            <dt>Expires</dt>
            <dd className={document.isExpired ? styles.expiredText : undefined}>{formatCalendarDate(document.expiryDate)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Uploaded</dt>
          <dd>{formatTimestamp(document.uploadedAt)}</dd>
        </div>
      </dl>

      {document.description ? <p className={listStyles.notes}>{document.description}</p> : null}

      {canManage ? (
        <div className={styles.cardActions}>
          <button type="button" className={formStyles.primary} onClick={() => void handleDownload()} disabled={downloadState.status === 'opening'}>
            {downloadState.status === 'opening' ? 'Opening…' : 'Download'}
          </button>
          <button type="button" className={factStyles.editToggle} onClick={() => setReplacing((open) => !open)}>
            {replacing ? 'Cancel' : 'Replace'}
          </button>
          <button type="button" className={styles.removeToggle} onClick={() => setRemoving((open) => !open)}>
            {removing ? 'Cancel' : 'Remove'}
          </button>
        </div>
      ) : null}

      {downloadState.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {downloadState.message}
        </p>
      ) : null}

      {replacing ? (
        <DocumentReplaceForm
          employeeId={employeeId}
          document={document}
          onDone={() => {
            setReplacing(false);
            onChanged();
          }}
        />
      ) : null}

      {removing ? (
        <form
          className={formStyles.form}
          aria-label={`Remove ${document.documentTypeName}`}
          onSubmit={(event) => {
            event.preventDefault();
            void handleRemove();
          }}
        >
          <div className={formStyles.field}>
            <label htmlFor={`document-remove-reason-${document.id}`} className={formStyles.label}>
              Reason for removing this document
            </label>
            <textarea
              id={`document-remove-reason-${document.id}`}
              className={formStyles.textarea}
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={state.status === 'saving'}
              required
            />
          </div>
          {state.status === 'failed' ? (
            <p className={formStyles.error} role="alert">
              {state.message}
            </p>
          ) : null}
          <div className={formStyles.actions}>
            <button type="submit" className={formStyles.primary} disabled={state.status === 'saving' || reason.trim().length === 0}>
              {state.status === 'saving' ? 'Removing…' : 'Confirm removal'}
            </button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

function DocumentUploadForm({
  employeeId,
  documentTypes,
  onDone,
  onClose,
}: {
  employeeId: string;
  documentTypes: DocumentTypeLibraryResponse['documentTypes'];
  onDone: () => void;
  onClose: () => void;
}) {
  const [documentTypeId, setDocumentTypeId] = useState(documentTypes[0]?.id ?? '');
  const [description, setDescription] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<{ status: 'idle' } | { status: 'saving' } | { status: 'failed'; message: string }>({
    status: 'idle',
  });

  async function submit() {
    if (!file || !documentTypeId) return;
    setState({ status: 'saving' });
    try {
      await apiUpload(`/employees/${employeeId}/documents`, file, {
        documentTypeId,
        description: description.trim() || null,
        issueDate: issueDate || null,
        expiryDate: expiryDate || null,
      });
      onDone();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form
      className={formStyles.form}
      aria-label="Upload document"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {documentTypes.length === 0 ? (
        <p className={formStyles.perspective}>No active document types exist yet — an administrator must define one first.</p>
      ) : (
        <div className={formStyles.field}>
          <label htmlFor="document-type" className={formStyles.label}>
            Document type
          </label>
          <select id="document-type" className={styles.select} value={documentTypeId} onChange={(event) => setDocumentTypeId(event.target.value)}>
            {documentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({CATEGORY_LABEL[type.category] ?? type.category})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={formStyles.field}>
        <label htmlFor="document-file" className={formStyles.label}>
          File (PDF, JPEG, or PNG)
        </label>
        <input
          id="document-file"
          type="file"
          accept={ACCEPTED_TYPES}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          disabled={state.status === 'saving'}
        />
      </div>

      <div className={formStyles.field}>
        <label htmlFor="document-description" className={formStyles.label}>
          Description
        </label>
        <textarea
          id="document-description"
          className={formStyles.textarea}
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={state.status === 'saving'}
        />
      </div>

      <div className={styles.dateRow}>
        <div className={formStyles.field}>
          <label htmlFor="document-issue-date" className={formStyles.label}>
            Issue date (optional)
          </label>
          <input
            id="document-issue-date"
            type="date"
            value={issueDate}
            onChange={(event) => setIssueDate(event.target.value)}
            disabled={state.status === 'saving'}
          />
        </div>
        <div className={formStyles.field}>
          <label htmlFor="document-expiry-date" className={formStyles.label}>
            Expiry date (optional)
          </label>
          <input
            id="document-expiry-date"
            type="date"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.target.value)}
            disabled={state.status === 'saving'}
          />
        </div>
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={state.status === 'saving' || !file || !documentTypeId}>
          {state.status === 'saving' ? 'Uploading…' : 'Upload document'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={state.status === 'saving'}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function DocumentReplaceForm({
  employeeId,
  document,
  onDone,
}: {
  employeeId: string;
  document: EmployeeDocumentResponse;
  onDone: () => void;
}) {
  const [description, setDescription] = useState(document.description ?? '');
  const [issueDate, setIssueDate] = useState(document.issueDate ?? '');
  const [expiryDate, setExpiryDate] = useState(document.expiryDate ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<{ status: 'idle' } | { status: 'saving' } | { status: 'failed'; message: string }>({
    status: 'idle',
  });

  async function submit() {
    if (!file) return;
    setState({ status: 'saving' });
    try {
      await apiUpload(`/employees/${employeeId}/documents/${document.id}/replacement`, file, {
        description: description.trim() || null,
        issueDate: issueDate || null,
        expiryDate: expiryDate || null,
      });
      onDone();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form
      className={formStyles.form}
      aria-label={`Replace ${document.documentTypeName}`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className={formStyles.perspective}>
        This records a <strong>replacement</strong> for the current {document.documentTypeName}. The earlier record is kept and stays on
        record; it stops counting as current.
      </p>

      <div className={formStyles.field}>
        <label htmlFor={`document-replace-file-${document.id}`} className={formStyles.label}>
          New file (PDF, JPEG, or PNG)
        </label>
        <input
          id={`document-replace-file-${document.id}`}
          type="file"
          accept={ACCEPTED_TYPES}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          disabled={state.status === 'saving'}
        />
      </div>

      <div className={formStyles.field}>
        <label htmlFor={`document-replace-description-${document.id}`} className={formStyles.label}>
          Description
        </label>
        <textarea
          id={`document-replace-description-${document.id}`}
          className={formStyles.textarea}
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={state.status === 'saving'}
        />
      </div>

      <div className={styles.dateRow}>
        <div className={formStyles.field}>
          <label htmlFor={`document-replace-issue-${document.id}`} className={formStyles.label}>
            Issue date (optional)
          </label>
          <input
            id={`document-replace-issue-${document.id}`}
            type="date"
            value={issueDate}
            onChange={(event) => setIssueDate(event.target.value)}
            disabled={state.status === 'saving'}
          />
        </div>
        <div className={formStyles.field}>
          <label htmlFor={`document-replace-expiry-${document.id}`} className={formStyles.label}>
            Expiry date (optional)
          </label>
          <input
            id={`document-replace-expiry-${document.id}`}
            type="date"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.target.value)}
            disabled={state.status === 'saving'}
          />
        </div>
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={state.status === 'saving' || !file}>
          {state.status === 'saving' ? 'Saving…' : 'Record replacement'}
        </button>
      </div>
    </form>
  );
}
